import "./NavigationMenu.less";

import { Button, Drawer, Icon, Menu } from "antd";
import ElementQueries, { ResizeSensor } from "css-element-queries";
import * as React from "react";
import { Redirect } from "react-router";

import * as Api from "lib/api";
import { CancellationToken } from "lib/api/base";
import { ContentSection } from "lib/data";
import { IBaseProps } from "lib/data/interfaces";
import { LoginService, StateService } from "lib/services";

import { LoginPage } from "components/pages";
import { ProfileIndicator } from "../ProfileIndicator";

interface INavigationMenuProps extends IBaseProps {
	contentSections?: ContentSection[];
}

interface INavigationMenuState extends IBaseProps {
	isContentSectionListModified: boolean;
	isNavigating: boolean;
	menuWidth: number;
	htmlWidth: number;
	displayMobileButton: boolean;
	displayMobileMenu: boolean;
}


export class NavigationMenu extends React.Component<INavigationMenuProps, INavigationMenuState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	private contentSections: ContentSection[] = [];
	private navigationDestination: string | undefined;
	private menuSensor?: ResizeSensor;
	private htmlSensor?: ResizeSensor;
	private menuMaxWidth: number = 0;

	constructor(props: INavigationMenuProps) {

		super(props);

		this.state = {
			isContentSectionListModified: false,
			isNavigating: false,
			menuWidth: 0,
			htmlWidth: 0,
			displayMobileButton: false,
			displayMobileMenu: false
		};
	}

	public async componentWillMount() {

		// Check For Provided Content Sections
		if (!this.props.contentSections) {

			// Request Content Sections
			const content = await Api.ContentManagementApi.getContentSections();

			// Store Content Sections
			this.contentSections = content;
		}
		else {

			// Store Provided Content Sections
			this.contentSections = this.props.contentSections;
		}

		// Set State
		StateService.setCancelableState(() => this.setState({
			isContentSectionListModified: true
		}), this.apiCancellationToken);
	}

	public componentDidMount() {

		this.setupSensors();
	}

	public componentWillUnmount() {

		// detach sensors
		if (this.menuSensor !== undefined) {
			this.menuSensor.detach();
		}
		if (this.htmlSensor !== undefined) {
			this.htmlSensor.detach();
		}
	}

	public async componentWillReceiveProps(newProps: INavigationMenuProps) {

		// Check For Provided Content Sections
		if (!newProps.contentSections) {

			// Request Content Sections
			const content = await Api.ContentManagementApi.getContentSections();

			// Store Content Sections
			this.contentSections = content;
		}
		else {

			// Store Provided Content Sections
			this.contentSections = newProps.contentSections;
		}

		// Get Logged In Status
		const isLoggedIn = await LoginService.isLoggedIn();

		// Check If Logged In
		if (isLoggedIn) {

			// Get User
			const user = await LoginService.getUserIfExists();

			// Check User
			if (user !== undefined) {

				// Set State
				StateService.setCancelableState(() => this.setState({
					isNavigating: false,
					isContentSectionListModified: true
				}), this.apiCancellationToken);

				return;
			}
		}

		// Set State
		StateService.setCancelableState(() => this.setState({
			isNavigating: false,
			isContentSectionListModified: true
		}), this.apiCancellationToken);
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		if (this.htmlSensor === undefined || this.menuSensor === undefined) {
			this.setupSensors();
		}

		if (this.state.menuWidth > this.menuMaxWidth) {

			this.menuMaxWidth = this.state.menuWidth;
		}

		// Check For Navigation
		if (this.state.isNavigating && this.navigationDestination) {

			// Redirect To Home Content Section
			return <Redirect push={true} to={this.navigationDestination} />;
		}
		else {

			return (
				<div id="navigation-menu">

					<Drawer
						visible={this.state.displayMobileMenu}
						onClose={() => this.setState({displayMobileMenu: false})}
						className="mobile-menu-drawer"
					>
						<Menu className="mobile-menu">

							<Menu.Item onClick={() => this.navigate()}>
								Home&nbsp;&nbsp;<Icon type="home" />
							</Menu.Item>

							{/* Render content sections if you are not at the /admin route */}
							{this.processContentSections()}

							{/* Check For Login Page */}
							{
								window.location.pathname.endsWith(LoginPage.BasePath)
								?
								null
								:
								<React.Fragment>
									<ProfileIndicator />
								</React.Fragment>
							}
						</Menu>
					</Drawer>

					{
						this.htmlSensor !== undefined && this.menuSensor !== undefined ? (
							!this.state.displayMobileButton
							?
							<>
								<div className="menuItemWrapper">

									<Button
										className="borderless"
										ghost={true}
										size="large"
										onClick={() => this.navigate()}
									>
										Home
									</Button>
								</div>

								{/* Render content sections if you are not at the /admin route */}
								{this.processContentSections()}

								{/* Check For Login Page */}
								{window.location.pathname.endsWith(LoginPage.BasePath)
									?
									null
									:
									<React.Fragment>
										<span className="separator" />
										<ProfileIndicator />
									</React.Fragment>
								}
							</>
							:
							<Button
								icon="menu"
								size="large"
								onClick={() => this.setState({displayMobileMenu: true})}
								type="link"
								className="mobile-menu-button"
							/>
						) : null
					}
				</div>
			);
		}
	}

	/**
	 * Checks to see, given the current width of the page and menu width, if the menu should be mobile
	 */
	private checkMobileMenu = () => {

		if (this.state.htmlWidth / 2 < this.menuMaxWidth && this.state.displayMobileButton === false) {

			this.setState({displayMobileButton: true});
		}
		else if (this.state.htmlWidth / 2 > this.menuMaxWidth && this.state.displayMobileButton === true) {

			this.setState({displayMobileButton: false});
		}
	}

	/**
	 * Sets up sensors to detect width changes
	 */
	private setupSensors = () => {

		const menuElement = document.querySelector("#navigation-menu");
		const htmlElement = document.querySelector("html");

		if (this.menuSensor === undefined && menuElement !== null) {

			this.menuSensor = new ElementQueries.ResizeSensor(menuElement, () => {

				this.setState({
					menuWidth: menuElement.clientWidth
				});
				this.checkMobileMenu();
			});
		}

		if (this.htmlSensor === undefined && htmlElement !== null) {

			this.htmlSensor = new ElementQueries.ResizeSensor(htmlElement, () => {

				this.setState({
					htmlWidth: htmlElement.clientWidth
				});
				this.checkMobileMenu();
			});
		}
	}

	private processContentSections() {

		if (this.state.displayMobileButton) {

			return (
				this.contentSections.map((section, index) => (
					<Menu.Item key={index} onClick={() => this.onMenuItemClick(section)}>
						{section.title}
					</Menu.Item>
				))
			);
		}
		else {

			return (
				this.contentSections.map((section, index) => (

					<div className="menuItemWrapper" key={index}>

						<span className="separator" />

						<Button
							className="borderless"
							ghost={true}
							size="large"
							onClick={() => this.onMenuItemClick(section)}
						>
							{section.title}
						</Button>
					</div>
				))
			);
		}
	}

	private onMenuItemClick = (section: ContentSection) => {

		// Parse Destination
		const destination: string = `/#${section.title.replace(/[^a-zA-Z0-9 ]/, "").replace(" ", "-")}`;

		// Start Navigation
		this.navigate(destination);
	}

	private navigate = (destination?: string) => {

		// Check Destination
		if (destination) {

			// Set Destination
			this.navigationDestination = destination;
		}
		else {

			// Set Destination To Root
			this.navigationDestination = "/";
		}

		// Indicate Page Is Navigating
		StateService.setCancelableState(() => this.setState({ isNavigating: true, displayMobileMenu: false }), this.apiCancellationToken);
	}
}
